/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Typography, IconButton, Dialog } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ColumnType, DataTable } from '../DataTable';
import { ConfirmationDialog } from '../commonComponents';
import { ListPaginationBar } from '../common/ListPagination';
import { useDebounce } from '../../utils/useDebounce';
import {
  AccessToggle,
  ActionMenuItem,
  RowActionsMenu,
  UserMgmtListToolbar,
  userMgmtBoxStyles,
  userMgmtNameLinkSx,
  userMgmtRowStyles,
  userMgmtTableBodyStyles,
  userMgmtTableHeaderStyles,
} from './userMgmtListStyles';

export const AccessHeader = (): JSX.Element => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
          color: 'inherit',
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
        component="span">
        Access
        <InfoOutlinedIcon
          color="info"
          fontSize="small"
          onClick={() => setOpen(true)}
          sx={{ cursor: 'pointer', position: 'relative', top: 1 }}
        />
      </Typography>

      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 550,
            p: { xs: 2, sm: '15px 20px 45px' },
            borderRadius: '10px',
            height: 'unset',
            overflowY: 'auto',
          },
        }}>
        <IconButton onClick={() => setOpen(false)} sx={{ alignSelf: 'flex-end' }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
          <InfoOutlinedIcon color="info" fontSize="large" />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
              textAlign: 'center',
            },
          }}>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px' }}>
            Disabling the access of the user will log him out of the active session and he will not
            be able to access the portal.
          </Typography>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px', mt: 2 }}>
            Please make sure there are no draft and In-progress applications for the user before
            disabling the user .
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export const NameLinkCell = ({
  label,
  to,
}: {
  label: string;
  to: { pathname: string; state?: Record<string, unknown> };
}): JSX.Element => (
  <Typography sx={userMgmtNameLinkSx} component={RouterLink} to={to}>
    {label || 'N/A'}
  </Typography>
);

export type UserMgmtListPageProps<T extends { id?: number | string; isActive?: boolean }> = {
  /** Loads list data. Return the array to display. */
  loadData: () => Promise<T[]>;
  /** Optional dependency array to re-fetch (e.g. distributor type). */
  loadDeps?: unknown[];
  /** Optional side-effect after load (e.g. set access flags). */
  onLoaded?: (rows: T[]) => void | Promise<void>;
  /** Build table columns. Receives helpers for access/actions. */
  getColumns: (helpers: {
    onToggleAccess: (row: T) => void;
    accessDisabled: boolean;
  }) => ColumnType[];
  /** Client-side search filter. */
  filterRow: (row: T, query: string) => boolean;
  searchPlaceholder: string;
  inviteLabel: string;
  onInvite?: () => void;
  showInvite?: boolean;
  /** Extra toolbar content (e.g. type select). */
  toolbarExtra?: ReactNode;
  /** Persist access toggle. Should return the updated row. */
  onAccessChange?: (row: T, nextActive: boolean) => Promise<T>;
  /** Disable access toggle globally. */
  accessDisabled?: boolean;
  /** Merge updated fields into existing row after access change. */
  mergeUpdatedRow?: (current: T, updated: T) => T;
};

export default function UserMgmtListPage<T extends { id?: number | string; isActive?: boolean }>({
  loadData,
  loadDeps = [],
  onLoaded,
  getColumns,
  filterRow,
  searchPlaceholder,
  inviteLabel,
  onInvite,
  showInvite = true,
  toolbarExtra,
  onAccessChange,
  accessDisabled = false,
  mergeUpdatedRow,
}: UserMgmtListPageProps<T>): JSX.Element {
  const [rows, setRows] = useState<T[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const debounceSearch = useDebounce(search, 300);
  const [pendingRow, setPendingRow] = useState<T | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await loadData();
        if (!alive) return;
        setRows(data || []);
        await onLoaded?.(data || []);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, loadDeps);

  const filteredRows = useMemo(() => {
    const q = debounceSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => filterRow(row, q));
  }, [rows, debounceSearch, filterRow]);

  const noOfPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage) || 1);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);

  useEffect(() => {
    if (currentPage > noOfPages) setCurrentPage(1);
  }, [noOfPages, currentPage]);

  const handlePageChange = (_event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const columns = useMemo(
    () =>
      getColumns({
        onToggleAccess: (row) => setPendingRow(row),
        accessDisabled,
      }),
    [getColumns, accessDisabled]
  );

  return (
    <>
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
        <UserMgmtListToolbar
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setCurrentPage(1);
          }}
          searchPlaceholder={searchPlaceholder}
          inviteLabel={inviteLabel}
          showInvite={showInvite}
          onInvite={onInvite}
          extraStart={toolbarExtra}
        />
        <DataTable
          tableData={pagedRows}
          tableHeader={columns}
          isLoading={isLoading}
          currentPage={undefined}
          noOfPages={1}
          renderAdditionalRow={false}
          tableBodyCustomStyles={userMgmtTableBodyStyles}
          rowCustomStyles={userMgmtRowStyles}
          tableHeaderCustomStyles={userMgmtTableHeaderStyles}
          boxStyles={userMgmtBoxStyles}
        />
        {!isLoading && filteredRows.length > 0 && (
          <ListPaginationBar
            pageCount={noOfPages}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(rows) => {
              setRowsPerPage(rows);
              setCurrentPage(1);
            }}
            total={filteredRows.length}
          />
        )}
      </Box>

      {onAccessChange && (
        <ConfirmationDialog
          message={`Are you sure you want to ${
            pendingRow?.isActive ? 'disable' : 'enable'
          } the access to this user ?`}
          open={pendingRow !== null}
          setOpen={() => setPendingRow(null)}
          onSave={async () => {
            const row = pendingRow;
            setPendingRow(null);
            if (!row) return;
            try {
              const updated = await onAccessChange(row, !row.isActive);
              setRows((prev) =>
                prev.map((item) => {
                  if (Number(item.id) !== Number(updated.id)) return item;
                  return mergeUpdatedRow ? mergeUpdatedRow(item, updated) : { ...item, ...updated };
                })
              );
            } catch (e) {
              console.error((e as Error).message);
            }
          }}
          onCancel={() => setPendingRow(null)}
        />
      )}
    </>
  );
}

/** Helper to build a standard Access column */
export const buildAccessColumn = <T extends { isActive?: boolean }>(
  onToggleAccess: (row: T) => void,
  accessDisabled = false
): ColumnType => ({
  header: AccessHeader,
  align: 'left',
  headerAlign: 'left',
  minWidth: 140,
  renderCell: (row: T) => (
    <AccessToggle
      checked={!!row.isActive}
      onChange={() => onToggleAccess(row)}
      disabled={accessDisabled}
    />
  ),
});

/** Helper to build a standard Actions column */
export const buildActionsColumn = <T,>(getItems: (row: T) => ActionMenuItem[]): ColumnType => ({
  header: 'Actions',
  renderCell: (row: T) => <RowActionsMenu items={getItems(row)} />,
});

/** Shared text search across common user fields */
export const matchesUserSearch = (
  query: string,
  fields: Array<string | number | null | undefined>
): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((field) =>
    String(field ?? '')
      .toLowerCase()
      .includes(q)
  );
};

export const formatUserName = (user?: Record<string, unknown> | null): string => {
  if (!user) return 'N/A';
  if (typeof user.name === 'string' && user.name) return user.name;
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || 'N/A';
};

export const formatPhone = (user?: Record<string, unknown> | null): string => {
  if (!user) return 'N/A';
  const phone = user.phone != null ? String(user.phone) : '';
  const countryCode = user.countryCode != null ? String(user.countryCode) : '';
  if (!phone) return 'N/A';
  return countryCode ? `${countryCode}-${phone}` : phone;
};
